// src/pages/FeedbackForm.tsx
import React, { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Stack,
  TextField, // <-- NUOVO
} from '@mui/material';
import { sendFeedback } from '../services/feedbackService';

type MoodKey = 'low' | 'medium' | 'high';

const moodOptions: Array<{ key: MoodKey; label: string; score: number; color: 'error' | 'warning' | 'success' }> = [
  { key: 'low',    label: 'Basso',  score: 1,  color: 'error' },
  { key: 'medium', label: 'Medio',  score: 6,  color: 'warning' },
  { key: 'high',   label: 'Alto',   score: 10, color: 'success' },
];

function FaceIcon({ mood, active }: { mood: MoodKey; active: boolean }) {
  const common = { stroke: 'currentColor', strokeWidth: 2, fill: 'none' as const, strokeLinecap: 'round' as const };
  const eyeFill = { fill: 'currentColor', opacity: active ? 1 : 0.7 };
  const strokeOpacity = active ? 1 : 0.7;

  return (
    <Box component="svg" viewBox="0 0 48 48" sx={{ width: 44, height: 44, display: 'block' }}>
      <circle cx="24" cy="24" r="20" {...common} style={{ opacity: strokeOpacity }} />
      <circle cx="16" cy="20" r="2" {...eyeFill} />
      <circle cx="32" cy="20" r="2" {...eyeFill} />

      {/* Basso = bocca all’insù (smorfia) */}
      {mood === 'low' && (
        <path d="M16 30 C 20 24, 28 24, 32 30" {...common} />
      )}

      {/* Medio = linea dritta */}
      {mood === 'medium' && (
        <path d="M16 30 L 32 30" {...common} />
      )}

      {/* Alto = bocca all’ingiù (sorriso) */}
      {mood === 'high' && (
        <path d="M16 30 C 20 36, 28 36, 32 30" {...common} />
      )}
    </Box>
  );
}

const FeedbackForm: React.FC = () => {
  const { callId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [selectedMood, setSelectedMood] = useState<MoodKey>('medium');
  const [score, setScore] = useState<number>(6);
  const [comment, setComment] = useState<string>(''); // <-- MODIFICATO (rimosso commento)
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleSelectMood = (key: MoodKey) => {
    const opt = moodOptions.find(o => o.key === key)!;
    setSelectedMood(key);
    setScore(opt.score);
  };

  const handleSubmit = async () => {
    if (!token || !callId) {
      setError('Token o ID chiamata mancante.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // 'comment' viene già inviato correttamente
      const res = await sendFeedback(callId, { token, score, comment });
      if (res.data.status === 'ok') {
        setSubmitted(true);
      } else {
        setError('Errore durante l’invio del feedback.');
      }
    } catch (err: any) {
      console.error(err);
      const msg = err.response?.data?.error || 'Errore durante l’invio.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Box maxWidth={520} mx="auto" mt={6}>
        <Alert severity="success">Grazie per il tuo feedback!</Alert>
      </Box>
    );
  }

  return (
    <Box maxWidth={520} mx="auto" mt={6}>
      <Typography variant="h5" gutterBottom>
        Lascia un feedback
      </Typography>

      <Typography variant="body1" sx={{ mb: 2 }}>
        Quanto sei soddisfatto?
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        {moodOptions.map(opt => {
          const selected = selectedMood === opt.key;
          return (
            <Paper
              key={opt.key}
              variant="outlined"
              onClick={() => !loading && handleSelectMood(opt.key)}
              sx={{
                flex: 1,
                cursor: loading ? 'default' : 'pointer',
                p: 2,
                borderRadius: 2,
                textAlign: 'center',
                transition: 'all 0.15s ease',
                borderColor: selected ? `${opt.color}.main` : 'divider',
                bgcolor: selected ? `${opt.color}.light` : 'background.paper',
                color: selected ? 'common.white' : 'text.primary',
                '&:hover': {
                  transform: loading ? 'none' : 'translateY(-2px)',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 1,
                  color: selected ? 'common.white' : 'text.secondary',
                }}
              >
                <FaceIcon mood={opt.key} active={selected} />
              </Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: selected ? 600 : 500 }}
              >
                {opt.label}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      {/* --- BLOCCO MODIFICATO --- */}
      {/* Sostituito input nascosto con TextField visibile */}
      <TextField
        label="Lascia un commento (opzionale)"
        variant="outlined"
        fullWidth
        multiline
        rows={4}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        disabled={loading}
        sx={{ mb: 2 }}
      />
      {/* --- FINE BLOCCO MODIFICATO --- */}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        fullWidth
        sx={{ py: 1.25 }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Invia'}
      </Button>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
        Selezione: {moodOptions.find(o => o.key === selectedMood)?.label} ({score}/10)
      </Typography>
    </Box>
  );
};

export default FeedbackForm;